﻿import { toast } from 'sonner';
import i18next from 'i18next';
import { useBigdesCompensatoryControlStore } from '@/store/bigdes-compensatory-control.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class BigdesCompensatoryControlIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

    async fetchAll(page = 1, pageSize = 10, search = '', sortBy = 'controlNo', sortOrder = 'asc', columnFilters = {}) {
        const store = useBigdesCompensatoryControlStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) acc[`filter_${key}`] = value;
                    return acc;
                }, {}),
            });

            store.setItems(response.data || []);
            if (response.pagination) {
                store.setPaginationData({
                    currentPage: response.pagination.page,
                    totalPages: response.pagination.totalPages,
                    totalItems: response.pagination.total,
                    itemsPerPage: response.pagination.pageSize,
                });
            }
            if (response.uniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async create(data) {
        const store = useBigdesCompensatoryControlStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.create(data);
            toast.success(
                i18next.t('bigdesCompensatoryControl:messages.createSuccess', {
                    defaultValue: 'Compensatory control created',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async update(id, data) {
        const store = useBigdesCompensatoryControlStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.update(id, data);
            toast.success(
                i18next.t('bigdesCompensatoryControl:messages.updateSuccess', {
                    defaultValue: 'Compensatory control updated',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async delete(id) {
        const store = useBigdesCompensatoryControlStore.getState();
        store.setLoading(true);

        try {
            await this.service.delete(id);
            toast.success(
                i18next.t('bigdesCompensatoryControl:messages.deleteSuccess', {
                    defaultValue: 'Compensatory control deleted',
                }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }
}
